import axios, { AxiosError } from 'axios';
import { AlpacaConfig, AlpacaPosition, AlpacaAccount, Trade, Bar } from '../types/brokers';

class AlpacaService {
  private config: AlpacaConfig | null = null;
  private baseUrl: string = '';
  private dataUrl: string = '';

  initialize(config: AlpacaConfig) {
    try {
      this.config = config;
      this.baseUrl = config.paper ? 'https://paper-api.alpaca.markets' : 'https://api.alpaca.markets';
      this.dataUrl = 'https://data.alpaca.markets';
    } catch (error) {
      console.error('Error initializing Alpaca service:', error);
      throw error;
    }
  }

  private getHeaders() {
    if (!this.config) {
      throw new Error('Alpaca service not initialized');
    }
    return {
      'APCA-API-KEY-ID': this.config.apiKey,
      'APCA-API-SECRET-KEY': this.config.secretKey
    };
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof AxiosError && error.response?.data?.message) {
      return error.response.data.message;
    }
    return error instanceof Error ? error.message : 'An unknown error occurred';
  }

  async getAccount(): Promise<AlpacaAccount> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/account`,
        { headers: this.getHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching Alpaca account:', error);
      throw new Error('Failed to get account: ' + this.getErrorMessage(error));
    }
  }

  async getPositions(): Promise<AlpacaPosition[]> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/positions`,
        { headers: this.getHeaders() }
      );

      return response.data.map((pos: any): AlpacaPosition => ({
        symbol: pos.symbol,
        qty: parseFloat(pos.qty),
        currentPrice: parseFloat(pos.current_price),
        entryPrice: parseFloat(pos.avg_entry_price),
        marketValue: parseFloat(pos.market_value),
        unrealizedPL: parseFloat(pos.unrealized_pl),
        unrealizedPLPercent: parseFloat(pos.unrealized_plpc)
      }));
    } catch (error) {
      console.error('Error fetching positions:', error);
      throw new Error('Failed to get positions: ' + this.getErrorMessage(error));
    }
  }

  async getPosition(symbol: string): Promise<AlpacaPosition> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/positions/${symbol}`,
        { headers: this.getHeaders() }
      );
      const pos = response.data;
      return {
        symbol: pos.symbol,
        qty: parseFloat(pos.qty),
        currentPrice: parseFloat(pos.current_price),
        entryPrice: parseFloat(pos.avg_entry_price),
        marketValue: parseFloat(pos.market_value),
        unrealizedPL: parseFloat(pos.unrealized_pl),
        unrealizedPLPercent: parseFloat(pos.unrealized_plpc)
      };
    } catch (error) {
      console.error('Error fetching position:', error);
      throw new Error('Failed to get position: ' + this.getErrorMessage(error));
    }
  }

  async getTrades(): Promise<Trade[]> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/v2/orders`,
        { headers: this.getHeaders() }
      );
      return response.data.map((trade: any): Trade => ({
        id: trade.id,
        symbol: trade.symbol,
        side: trade.side,
        qty: parseFloat(trade.qty),
        price: parseFloat(trade.filled_avg_price || trade.limit_price || '0'),
        avgPrice: trade.filled_avg_price ? parseFloat(trade.filled_avg_price) : undefined,
        timestamp: trade.submitted_at,
        strategy: trade.client_order_id || 'manual',
        executed_at: trade.filled_at
      }));
    } catch (error) {
      console.error('Error fetching trades:', error);
      throw new Error('Failed to get trades: ' + this.getErrorMessage(error));
    }
  }

  async getBars(symbol: string, timeframe: string = '1D', limit: number = 100): Promise<Bar[]> {
    try {
      const response = await axios.get(
        `${this.dataUrl}/v2/stocks/${symbol}/bars`,
        {
          headers: this.getHeaders(),
          params: {
            timeframe,
            limit
          }
        }
      );
      return response.data.bars.map((bar: any): Bar => ({
        t: bar.t,
        o: parseFloat(bar.o),
        h: parseFloat(bar.h),
        l: parseFloat(bar.l),
        c: parseFloat(bar.c),
        v: parseInt(bar.v, 10)
      }));
    } catch (error) {
      console.error('Error fetching bars:', error);
      throw new Error('Failed to get bars: ' + this.getErrorMessage(error));
    }
  }
}

export const alpacaService = new AlpacaService();
